import { Add, ArrowBack } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	Divider,
	FormControlLabel,
	FormGroup,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import { ContactType } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import {
	BoundAsyncSelect,
	BoundNumberField,
	BoundTextField,
	useAsyncSelectProjectQuery,
	useAsyncSelectContactQuery,
	useAsyncSelectMembersWithEmailQuery,
} from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import InvoiceItemComponent from "../components/InvoiceItem";
import { InvoiceSummary } from "../components/InvoiceSummary";
import { useCreditNoteValidator } from "../hooks/InvoiceValidationHooks";
import { useCreateCreditNoteHook } from "../hooks/CreateCreditNoteHook";
import { useUpdateCreditNoteHook } from "../hooks/UpdateCreditNoteHook";

import type { CreditNote, Invoice, InvoiceItem } from "../types/CommercialDocumentTypes";
import type { RuleFactoryType } from "@repo/server-api/types";
import type { SetStateAction } from "react";

export type CreditNoteEditProps = {
	creditNote: CreditNote;
	setCreditNote: (value: SetStateAction<CreditNote>) => void;
	rulefactory?: RuleFactoryType;
};

const SectionHeader = ({ title }: { title: string }) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 3 }}>
		<Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
			{title.toUpperCase()}
		</Typography>
		<Divider sx={{ flexGrow: 1 }} />
	</Box>
);

const CreditNoteEdit = (props: CreditNoteEditProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { creditNote, setCreditNote } = props;
	const { errors, setErrors, validate } = useCreditNoteValidator();

	const useBinding = <TValue,>(selector: (m: CreditNote) => TValue) => {
		return useBoundDeep(creditNote, setCreditNote, selector, errors, setErrors);
	};

	// Supplier is selected but mapped to the client field
	const supplierField = useBinding((cn) => cn.client);
	const supplierContactPersonField = useBinding((cn) => cn.contactPerson);
	const contractorField = useBinding((cn) => cn.contractor);
	const projectField = useBinding((cn) => cn.project);

	const searchSupplierHook = useAsyncSelectContactQuery([ContactType.Supplier]);
	const searchSupplierContactPersonHook = useAsyncSelectMembersWithEmailQuery(
		creditNote.client?.id ?? null,
		[ContactType.ContactPerson],
	);
	const searchContractorHook = useAsyncSelectContactQuery([ContactType.Contractor]);
	const searchProjectHook = useAsyncSelectProjectQuery();

	// Clear contact person when supplier changes
	const [loaded, setLoaded] = useState(false);
	useEffect(() => {
		if (!loaded) {
			setLoaded(true);
			return;
		}
		supplierContactPersonField.onChangeValue(null);
		searchSupplierContactPersonHook.resetSearch();
		projectField.onChangeValue(null);
		searchProjectHook.resetSearch();
	}, [supplierField.value?.id]);

	const headerNoteField = useBinding((cn) => cn.headerNote);
	const footerNoteField = useBinding((cn) => cn.footerNote);
	const paymentDeadlineDaysField = useBinding((cn) => cn.paymentDeadlineDays);

	const { updateCreditNote, loading: updating } = useUpdateCreditNoteHook({
		onSaved: (updatedCreditNote) => {
			setCreditNote(updatedCreditNote);
		},
	});

	const { save: createCreditNote, loading: creating } = useCreateCreditNoteHook({
		onSaved: (newCreditNoteId: string) => {
			setCreditNote((prev) => ({ ...prev, id: newCreditNoteId }));
			navigate(`/vouchers/draft/${encodeURIComponent(newCreditNoteId)}`, { replace: true });
		},
	});

	const handleSubmit = async () => {
		const { isValid, errors } = await validate(creditNote);

		// if (!isValid || updating || creating) {
		if (updating || creating) {
			return;
		}

		if (creditNote.id) updateCreditNote(creditNote);
		else createCreditNote(creditNote);
	};

	const handleCancel = () => {
		navigate(-1);
	};

	const handleAddItem = () => {
		const newItem = {
			id: crypto.randomUUID(),
			name: "",
			description: "",
			quantity: 1,
			unit: "",
			unitPrice: 0,
			discount: 0,
			totalPrice: 0,
			tax: 0.19,
			taxCode: "U19",
		};
		setCreditNote((prev) => ({
			...prev,
			items: [...(prev.items || []), newItem as unknown as InvoiceItem],
		}));
		setErrors((prevErrors) => ({
			...prevErrors,
			items: "",
		}));
	};

	return (
		<Box sx={{ minHeight: "90vh", p: 3 }}>
			{/* Header */}
			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
				<Button className="left" variant="outlined" onClick={handleCancel}>
					<ArrowBack />
				</Button>
				<Typography className="left" variant="h5" color="primary">
					<span className="left" style={{ marginTop: "7px" }}>
						{creditNote.id ? t("credit-note-edit") : t("create-credit-note")}
					</span>
					<FormGroup sx={{ ml: 2.5, p: 0.5 }} className="left">
						<FormControlLabel
							control={
								<Switch
									onChange={(event) => {
										setCreditNote({
											...creditNote,
											isProforma: event.target.checked,
										});
									}}
									checked={creditNote.isProforma || false}
								/>
							}
							label={t("is-proforma")}
						/>
					</FormGroup>
				</Typography>
			</Stack>

			{/* Global Information Section */}
			<SectionHeader title={t("global-information")} />

			<Stack spacing={3}>
				{/* Supplier selection (mapped to client field) */}
				<BoundAsyncSelect
					label={t("supplier")}
					boundedValue={supplierField}
					searchHook={searchSupplierHook}
				/>

				<BoundAsyncSelect
					label={t("project")}
					boundedValue={projectField}
					searchHook={searchProjectHook}
				/>

				<BoundAsyncSelect
					label={t("supplier-contact-person")}
					boundedValue={supplierContactPersonField}
					searchHook={searchSupplierContactPersonHook}
				/>

				<BoundAsyncSelect
					label={t("contractors")}
					boundedValue={contractorField}
					searchHook={searchContractorHook}
				/>

				<BoundNumberField
					label={t("payment-deadline-days")}
					boundedValue={paymentDeadlineDaysField}
					additionalProps={{ sx: { maxWidth: 200 } }}
				/>
			</Stack>

			{/* Header Note Section */}
			<SectionHeader title={t("credit-note-header")} />

			<BoundTextField
				label={t("header-note")}
				boundedValue={headerNoteField}
				additionalProps={{
					rows: 4,
					multiline: true,
					fullWidth: true,
					placeholder: t("invoice-header-note-placeholder"),
					focused: true,
				}}
			/>

			{/* Items Section */}
			<SectionHeader title={t("articles")} />

			<InvoiceItemComponent
				invoice={creditNote as unknown as Invoice}
				setInvoice={setCreditNote as unknown as (value: SetStateAction<Invoice>) => void}
				errors={errors}
				setErrors={setErrors}
			/>

			<Stack direction="row" spacing={2} sx={{ mt: 2 }}>
				<Button data-testid="credit-note-add-item-button" variant="contained" color="success" onClick={handleAddItem}>
					{t("article")} <Add sx={{ ml: 1 }} />
				</Button>
			</Stack>

			<InvoiceSummary items={creditNote.items} />

			{/* Footer Note Section */}
			<SectionHeader title={t("credit-note-footer")} />

			<BoundTextField
				label={t("footer-note")}
				boundedValue={footerNoteField}
				additionalProps={{ rows: 4, multiline: true, fullWidth: true }}
			/>

			{/* Save Button */}
			<Stack direction="row" justifyContent="flex-end" sx={{ mt: 4, mb: 2 }}>
				<LoadingButton
					data-testid="credit-note-save-button"
					loading={updating || creating}
					variant="contained"
					color="primary"
					onClick={handleSubmit}
					size="large">
					{t("save")}
				</LoadingButton>
			</Stack>
		</Box>
	);
};

export default CreditNoteEdit;
